import { FC, useEffect, useRef, useState } from 'react'

import cn from 'classnames'

import styles from './ViewportMount.module.scss'
import { ViewportMountProps } from './ViewportMount.types'

const visibilityCallbacks = new Map<Element, (isNear: boolean) => void>()
let visibilityObserver: IntersectionObserver | null = null

const observe = (
  node: Element,
  onVisibilityChange: (isNear: boolean) => void
) => {
  if (!visibilityObserver) {
    visibilityObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          visibilityCallbacks.get(entry.target)?.(entry.isIntersecting)
        })
      },
      { rootMargin: '200px' }
    )
  }

  visibilityCallbacks.set(node, onVisibilityChange)
  visibilityObserver.observe(node)

  return () => {
    visibilityObserver?.unobserve(node)
    visibilityCallbacks.delete(node)
    if (visibilityCallbacks.size === 0) {
      visibilityObserver?.disconnect()
      visibilityObserver = null
    }
  }
}

// The wrapper keeps its place in the grid or sidebar while expensive content
// mounts near the visible area and unmounts after it leaves.
export const ViewportMount: FC<ViewportMountProps> = ({
  children,
  active = true,
  className,
  style
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isNear, setIsNear] = useState(
    () => typeof IntersectionObserver === 'undefined'
  )

  useEffect(() => {
    const node = wrapperRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return

    return observe(node, setIsNear)
  }, [])

  return (
    <div ref={wrapperRef} className={cn(styles.root, className)} style={style}>
      {active && isNear ? children : null}
    </div>
  )
}
